import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  CButton,
  CCard,
  CCardBody,
  CCol,
  CRow,
  CForm,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CFormCheck,
} from "@coreui/react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import {
  createStore,
  getSingleStore,
  updateStore,
} from "../../services/store.services";
import { useParams } from "react-router-dom";
import { getActiveCountryList } from "src/services/country.services";
import Select from "react-select";

let schema = z.object({
  locationCode: z.string().min(1, "Please type location code"),
  name: z.string().min(1, "Please type name"),
  latitude: z.string().min(1, "Please type latitude"),
  longitude: z.string().min(1, "Please type longitude"),
  address: z.string().min(1, "Please type address"),
  city: z.string().min(1, "Please type city"),
  state: z.string().min(1, "Please type state"),
  country: z.string().min(1, "Please type state"),
  zipCode: z.string().min(1, "Please type zip code"),
  contactName: z.string().min(1, "Please type contact name"),
  contactEmail: z.string().min(1, "Please type contact email"),
  contactPhone: z.string().min(1, "Please type contact phone"),
  contactAddress: z.string().min(1, "Please type contact address"),
});

const CreateStoreForm = () => {
  const [userInfo, setUserInfo] = useState(
    localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : null
  );
  const [storeId, setStoreId] = useState("");
  const [storeData, setStoreData] = useState(null);
  const [countryOptions, setCountryOptions] = useState([]);
  const [selectedCountryOption, setSelectedCountryOption] = useState(null);
  const [defaultErrorMessage, setDefaultErrorMessage] = useState(
    '"Something went wrong. Please try again"'
  );
  const { id } = useParams();

  useEffect(() => {
    if (id) {
      setStoreId(id);
      getStoreInfo(id);
    }
    console.log("id======", id);
  }, [id]);

  useEffect(() => {
    if (userInfo?.country) {
      let selectedOption = {
        value: userInfo.country._id,
        label: userInfo.country.name,
      };
      setSelectedCountryOption(selectedOption);
      setValue("country", selectedOption.value);
    }

    if (userInfo?.userType === "superAdmin") {
      getActiveCountry();
    }
  }, []);

  const getActiveCountry = () => {
    const limit = 100;
    const page = 1;
    getActiveCountryList({ limit, page, status: true })
      .then(async (response) => {
        const countryData = response.data;
        let options = [];
        countryData.forEach((element) => {
          const option = {
            value: `${element._id}`,
            label: `${element.name} - ${element.code}`,
          };
          options.push(option);
        });
        setCountryOptions(options);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const getStoreInfo = (id) => {
    // setIsLoading(true)
    getSingleStore(id)
      .then(async (response) => {
        setStoreData(response.data);
        const {
          locationCode,
          name,
          latitude,
          longitude,
          address,
          city,
          state,
          country,
          zipCode,
          contactName,
          contactEmail,
          contactPhone,
          contactAddress,
        } = response.data;

        if (locationCode) {
          setValue("locationCode", String(locationCode));
        }
        setValue("name", name);
        if (latitude) {
          setValue("latitude", latitude.toString());
        }
        if (longitude) {
          setValue("longitude", longitude.toString());
        }
        setValue("address", address);
        setValue("city", city);
        setValue("state", state);
        if (zipCode) {
          setValue("zipCode", zipCode.toString());
        }
        setValue("contactName", contactName);
        setValue("contactEmail", contactEmail);
        if (contactPhone) {
          setValue("contactPhone", contactPhone.toString());
        }
        setValue("contactAddress", contactAddress);

        if (country) {
          setValue("country", country?._id);
          setSelectedCountryOption({
            label: country?.name + " - " + country?.code,
            value: country?._id,
          });
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  // Perform any user-specific checks here
  if (userInfo?.userType === "superAdmin") {
    // Enable country selection for super admins
    schema = schema.extend({
      country: z.string().min(1, "Please type country"),
    });
  }

  const {
    register,
    setValue,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      locationCode: "",
      name: "",
      latitude: 0,
      longitude: 0,
      address: "",
      city: "",
      state: "",
      country: "",
      zipCode: 0,
      contactName: "",
      contactEmail: "",
      contactPhone: "",
      contactAddress: "",
    },
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data) => {
    if (storeId) {
      toast
        .promise(
          updateStore(data, storeId),
          {
            loading: "Loading",
            success: "Location Updated Succesfully",
            error: (err) => {
              return err.response &&
                err.response.data &&
                err.response.data.message
                ? err.response.data.message
                : defaultErrorMessage;
            },
          },
          {
            id: "store",
          }
        )
        .then(async (response) => {
          // console.log("response", response);
        })
        .catch((error) => console.error(error));
    } else {
      toast
        .promise(
          createStore(data),
          {
            loading: "Loading",
            success: "Location Created Succesfully",
            error: (err) => {
              return err.response &&
                err.response.data &&
                err.response.data.message
                ? err.response.data.message
                : defaultErrorMessage;
            },
          },
          {
            id: "store",
          }
        )
        .then(async (response) => {
          reset();
          setSelectedCountryOption("");
          setValue("country", "");
        })
        .catch((error) => console.error(error));
    }
  };

  const onError = (err) => {
    console.error(err);
  };

  const handleChangeCountry = (selectedOption) => {
    if (selectedOption) {
      setSelectedCountryOption(selectedOption);
      setValue("country", selectedOption.value);
    } else {
      setSelectedCountryOption("");
      setValue("country", "");
    }
  };

  return (
    <CCard className="mb-4">
      <CCardBody>
        <CRow>
          <CCol sm={5}>
            <h4 id="traffic" className="card-title mb-0">
              {storeId ? "Edit Location" : "Create Location"}
            </h4>
          </CCol>
          <CCol sm={7} className="d-none d-md-block"></CCol>
        </CRow>
        <hr />
        <br />
        <CForm onSubmit={handleSubmit(onSubmit, onError)}>
          <CRow>
            <CCol sm={6}>
              <CRow className="mb-3">
                <CFormLabel
                  htmlFor="locationCode"
                  className="col-sm-3 col-form-label"
                >
                  Location Code:
                </CFormLabel>
                <CCol sm={8}>
                  <CFormInput
                    type="text"
                    id="locationCode"
                    {...register("locationCode")}
                  />
                  {errors.locationCode?.message && (
                    <p className="error_text">{errors.locationCode?.message}</p>
                  )}
                </CCol>
              </CRow>
              <CRow className="mb-3">
                <CFormLabel htmlFor="name" className="col-sm-3 col-form-label">
                  Name:
                </CFormLabel>
                <CCol sm={8}>
                  <CFormInput type="text" id="name" {...register("name")} />
                  {errors.name?.message && (
                    <p className="error_text">{errors.name?.message}</p>
                  )}
                </CCol>
              </CRow>

              <CRow className="mb-3">
                <CFormLabel
                  htmlFor="country"
                  className="col-sm-3 col-form-label"
                >
                  Country:
                </CFormLabel>
                <CCol sm={8}>
                  <Select
                    value={selectedCountryOption}
                    onChange={handleChangeCountry}
                    options={countryOptions}
                    isClearable={true}
                    isDisabled={userInfo?.userType !== "superAdmin"}
                  />
                  {/* <CFormInput
                    type="text"
                    id="country"
                    {...register("country")}
                  /> */}
                  {errors.country?.message && (
                    <p className="error_text">{errors.country?.message}</p>
                  )}
                </CCol>
              </CRow>

              <CRow className="mb-3">
                <CFormLabel
                  htmlFor="latitude"
                  className="col-sm-3 col-form-label"
                >
                  Latitude:
                </CFormLabel>
                <CCol sm={8}>
                  <CFormInput
                    type="text"
                    id="latitude"
                    {...register("latitude")}
                  />
                  {errors.latitude?.message && (
                    <p className="error_text">{errors.latitude?.message}</p>
                  )}
                </CCol>
              </CRow>

              <CRow className="mb-3">
                <CFormLabel
                  htmlFor="longitude"
                  className="col-sm-3 col-form-label"
                >
                  Longitude:
                </CFormLabel>
                <CCol sm={8}>
                  <CFormInput
                    type="text"
                    id="longitude"
                    {...register("longitude")}
                  />
                  {errors.longitude?.message && (
                    <p className="error_text">{errors.longitude?.message}</p>
                  )}
                </CCol>
              </CRow>

              <CRow className="mb-3">
                <CFormLabel
                  htmlFor="address"
                  className="col-sm-3 col-form-label"
                >
                  Address:
                </CFormLabel>
                <CCol sm={8}>
                  <CFormInput
                    type="text"
                    id="address"
                    {...register("address")}
                  />
                  {errors.address?.message && (
                    <p className="error_text">{errors.address?.message}</p>
                  )}
                </CCol>
              </CRow>

              <CRow className="mb-3">
                <CFormLabel
                  htmlFor="zipCode"
                  className="col-sm-3 col-form-label"
                >
                  Zip Code:
                </CFormLabel>
                <CCol sm={8}>
                  <CFormInput
                    type="text"
                    id="zipCode"
                    {...register("zipCode")}
                  />
                  {errors.zipCode?.message && (
                    <p className="error_text">{errors.zipCode?.message}</p>
                  )}
                </CCol>
              </CRow>
              {/* <CRow className="mb-3">
                <CFormLabel htmlFor="image" className="col-sm-3 col-form-label">
                  Image:
                </CFormLabel>
                <CCol sm={8}>
                  <CFormInput
                    type="file"
                    accept="image/*"
                    id="image"
                    {...register("image")}
                  />
                  {errors.image?.message && (
                    <p className="error_text">{errors.image?.message}</p>
                  )}
                </CCol>
              </CRow> */}
            </CCol>
            <CCol sm={6}>
              <CRow className="mb-3">
                <CFormLabel htmlFor="city" className="col-sm-3 col-form-label">
                  City:
                </CFormLabel>
                <CCol sm={8}>
                  <CFormInput type="text" id="city" {...register("city")} />
                  {errors.city?.message && (
                    <p className="error_text">{errors.city?.message}</p>
                  )}
                </CCol>
              </CRow>

              <CRow className="mb-3">
                <CFormLabel htmlFor="state" className="col-sm-3 col-form-label">
                  State:
                </CFormLabel>
                <CCol sm={8}>
                  <CFormInput type="text" id="state" {...register("state")} />
                  {errors.state?.message && (
                    <p className="error_text">{errors.state?.message}</p>
                  )}
                </CCol>
              </CRow>

              <CRow className="mb-3">
                <CFormLabel
                  htmlFor="contactName"
                  className="col-sm-3 col-form-label"
                >
                  Contact Person Name:
                </CFormLabel>
                <CCol sm={8}>
                  <CFormInput
                    type="text"
                    id="contactName"
                    {...register("contactName")}
                  />
                  {errors.contactName?.message && (
                    <p className="error_text">{errors.contactName?.message}</p>
                  )}
                </CCol>
              </CRow>
              <CRow className="mb-3">
                <CFormLabel
                  htmlFor="contactEmail"
                  className="col-sm-3 col-form-label"
                >
                  Contact Person Email:
                </CFormLabel>
                <CCol sm={8}>
                  <CFormInput
                    type="email"
                    id="contactEmail"
                    {...register("contactEmail")}
                  />
                  {errors.contactEmail?.message && (
                    <p className="error_text">{errors.contactEmail?.message}</p>
                  )}
                </CCol>
              </CRow>

              <CRow className="mb-3">
                <CFormLabel
                  htmlFor="contactPhone"
                  className="col-sm-3 col-form-label"
                >
                  Contact Person Phone:
                </CFormLabel>
                <CCol sm={8}>
                  <CFormInput
                    type="text"
                    id="contactPhone"
                    {...register("contactPhone")}
                  />
                  {errors.contactPhone?.message && (
                    <p className="error_text">{errors.contactPhone?.message}</p>
                  )}
                </CCol>
              </CRow>
              <CRow className="mb-3">
                <CFormLabel
                  htmlFor="contactAddress"
                  className="col-sm-3 col-form-label"
                >
                  Contact Person Address:
                </CFormLabel>
                <CCol sm={8}>
                  <CFormInput
                    type="text"
                    id="contactAddress"
                    {...register("contactAddress")}
                  />
                  {errors.contactAddress?.message && (
                    <p className="error_text">
                      {errors.contactAddress?.message}
                    </p>
                  )}
                </CCol>
              </CRow>
            </CCol>

            <CRow className="mt-5">
              <div className="d-flex justify-content-end mt-3">
                <CButton type="submit" color="primary" className="px-5 py-2">
                  Submit
                </CButton>
              </div>
            </CRow>
          </CRow>
        </CForm>
      </CCardBody>
    </CCard>
  );
};

export default CreateStoreForm;
